"use client";

import { useMemo } from "react";
import Link from "next/link";
import { usePathname, useSearchParams } from "next/navigation";
import { DockSheet } from "@/components/ui/dock-sheet";
import { DialogCard } from "@/components/ui/dialog-card";
import { getFirstImageUrl } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { MiniListingCard } from "@/components/feed/mini-listing-card";
import { useTranslations } from "next-intl";
import { useMobile } from "@/hooks/use-mobile";

export interface BookmarkModalListing {
  id: string;
  title: string;
  price: string;
  currency?: string | null;
  // ADR_LISTING_SHIP_FROM_ADDRESS Phase 2: `location` removed — the modal
  // doesn't render any ship-from chip (only price/image/title preview).
  image_urls: string[] | string;
  description: string;
  condition: string;
  grade: string;
  created_at: string;
  username?: string;
  avatar_url?: string | null;
  scheduledPublishAt?: string | null;
}

interface BookmarkLoginModalProps {
  open: boolean;
  onClose: () => void;
  listing: BookmarkModalListing;
  variant?: "bookmark" | "buyNow";
}

export function BookmarkLoginModal({ open, onClose, listing, variant = "bookmark" }: BookmarkLoginModalProps) {
  const t = useTranslations("bookmarkModal");
  const isMobile = useMobile();

  const title = variant === "buyNow" ? t("buyNowTitle") : t("bookmarkTitle");
  const description = variant === "buyNow" ? t("buyNowDescription") : t("bookmarkDescription");

  const pathname = usePathname();
  const searchParams = useSearchParams();

  const callbackUrl = useMemo(() => {
    if (!pathname) return undefined;
    const query = searchParams?.toString();
    return query ? `${pathname}?${query}` : pathname;
  }, [pathname, searchParams]);

  const loginHref = callbackUrl ? `/login?callbackUrl=${encodeURIComponent(callbackUrl)}` : "/login";
  const registerHref = callbackUrl ? `/register?callbackUrl=${encodeURIComponent(callbackUrl)}` : "/register";

  const listingPreview = (
    <div className="flex justify-center">
      <MiniListingCard
        listingId={listing.id}
        image={getFirstImageUrl(listing.image_urls) || "/placeholder.svg"}
        name={listing.title}
        price={Number(listing.price) || 0}
        currency={listing.currency ?? null}
        scheduledPublishAt={listing.scheduledPublishAt ?? null}
        size="md"
        interactive={false}
      />
    </div>
  );

  const authActions = (
    <div className="flex flex-col items-center gap-3 w-full">
      <div className="w-full space-y-3">
        <Link href={loginHref} className="block">
          <Button className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90" size="lg">
            {t("logIn")}
          </Button>
        </Link>
        <Link href={registerHref} className="block">
          <Button
            variant="outline"
            className="w-full rounded-full border-foreground/30 text-foreground hover:bg-foreground/10 bg-transparent"
            size="lg"
          >
            {t("register")}
          </Button>
        </Link>
      </div>
      <p className="text-center text-xs text-muted-foreground/70">
        {t.rich("agreeTerms", {
          terms: (chunks) => (
            <Link href="/terms" className="underline hover:text-foreground" onClick={(e) => e.stopPropagation()}>
              {chunks}
            </Link>
          ),
          privacy: (chunks) => (
            <Link href="/privacy" className="underline hover:text-foreground" onClick={(e) => e.stopPropagation()}>
              {chunks}
            </Link>
          ),
        })}
      </p>
    </div>
  );

  if (!open) return null;

  if (isMobile) {
    return (
      <DockSheet
        open={open}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) onClose();
        }}
        title={title}
        zIndex={10100}
        belowTitle={listingPreview}
        contentSized
      >
        <div className="flex flex-col items-center gap-4 pb-2">
          <p className="text-sm text-muted-foreground text-center">{description}</p>
          {authActions}
        </div>
      </DockSheet>
    );
  }

  return (
    <DialogCard
      open={open}
      onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}
      title={title}
      panelClassName="max-w-lg"
    >
      <div className="flex flex-col items-center gap-4 sm:gap-6">
        <p className="text-sm text-muted-foreground text-center">{description}</p>
        {listingPreview}
        {authActions}
      </div>
    </DialogCard>
  );
}
